import React, { useState, useEffect, useCallback } from 'react';
import Board from './Board';
import Players from './Players';
import GameSidebar from './GameSidebar';
import { BOARD_SIZE, INITIAL_TIME, STONES_PER_PLAYER } from '../constants';
import { getPossibleMoves } from '../utils/movement';
import { findSeparatedCells } from '../utils/separated-cells';
import { getPossibleWalls, isValidWallPlacement } from '../utils/walls';
import { makeRandomMove } from '../ai/random';
import { makeNormalMove } from '../ai/normal';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);
  return matches;
}

const Game = ({ gameMode, playerColor, aiType }) => {
  const [board, setBoard] = useState(Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null)));
  const [walls, setWalls] = useState({
    horizontal: Array(BOARD_SIZE + 1).fill(null).map(() => Array(BOARD_SIZE).fill(null)),
    vertical: Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE + 1).fill(null))
  });
  const [currentPlayer, setCurrentPlayer] = useState('black');
  const [turnAction, setTurnAction] = useState('place'); // 'place', 'move', or 'wall'
  const [selectedStone, setSelectedStone] = useState(null);
  const [stonesPlaced, setStonesPlaced] = useState({ black: 0, white: 0 });
  const [totalStonesPlaced, setTotalStonesPlaced] = useState(0);
  const [timeLeft, setTimeLeft] = useState(INITIAL_TIME);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [scores, setScores] = useState(null);
  const [possibleMoves, setPossibleMoves] = useState([]);
  const [separatedCellsGroups, setSeparatedCellsGroups] = useState(null);
  const [possibleWalls, setPossibleWalls] = useState([]);
  const [currentStyle, setCurrentStyle] = useState('classic');

  // Check if a player has any legal moves
  const canMoveStone = useCallback((player) => {
    // During moving phase, check if any stone has valid moves
    for (let i = 0; i < BOARD_SIZE; i++) {
      for (let j = 0; j < BOARD_SIZE; j++) {
        if (board[i][j] !== player) continue

        const moves = getPossibleMoves(board, walls, i, j);
        if (moves.length > 0) return true
      }
    }
    return false
  }, [board, walls]);

  // Switch to next player, handling cases where they might have no legal moves
  const switchToNextPlayer = useCallback(() => {
    const nextPlayer = currentPlayer === 'black' ? 'white' : 'black';
    if (canMoveStone(nextPlayer)) setCurrentPlayer(nextPlayer)

    setTimeLeft(INITIAL_TIME)
    setSelectedStone(null)
  }, [currentPlayer, canMoveStone]);

  // Timer effect
  const handleTimeOut = useCallback(() => {
    setGameOver(true);
    setWinner(currentPlayer === 'black' ? 'white' : 'black');
  }, [currentPlayer]);

  useEffect(() => {
    if (gameOver) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleTimeOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameOver, handleTimeOut]);

  const handleWallClick = useCallback((type, row, col, isAIMove = false) => {
    if (gameOver || turnAction !== 'wall' || !selectedStone || (gameMode === 'ai' && currentPlayer !== playerColor && !isAIMove)) return;

    if (!isValidWallPlacement(walls, selectedStone, type, row, col)) return;

    const newWalls = { ...walls };
    if (type === 'horizontal') {
      newWalls.horizontal[row][col] = currentPlayer;
    } else {
      newWalls.vertical[row][col] = currentPlayer;
    }
    setWalls(newWalls);
    setTurnAction('move');
    setSelectedStone(null);
    switchToNextPlayer();
  }, [gameOver, turnAction, selectedStone, currentPlayer, walls, gameMode, playerColor, switchToNextPlayer]);

  const handleCellClick = useCallback((row, col, isAIMove = false) => {
    if (gameOver || (gameMode === 'ai' && currentPlayer !== playerColor && !isAIMove)) return;

    if (turnAction === 'place') {
      if (!board[row][col]) {
        const newBoard = [...board];
        newBoard[row][col] = currentPlayer;
        setBoard(newBoard);

        const newStonesPlaced = {
          ...stonesPlaced,
          [currentPlayer]: stonesPlaced[currentPlayer] + 1
        };
        setStonesPlaced(newStonesPlaced);
        setTotalStonesPlaced(totalStonesPlaced + 1);

        // Check if we need to move to the next phase
        if (totalStonesPlaced + 1 === STONES_PER_PLAYER * 2) {
          setTurnAction('move');
          setCurrentPlayer('black'); // Black always starts the moving phase
        } else {
          setCurrentPlayer(currentPlayer === 'black' ? 'white' : 'black')
        }

        setTimeLeft(INITIAL_TIME);
      }
    } else if (turnAction === 'move') {
      if (board[row][col] === currentPlayer && (!selectedStone || selectedStone.row !== row || selectedStone.col !== col)) {
        // Select the stone
        setSelectedStone({ row, col });
      } else if (selectedStone) {
        // Only allow moves to possibleMoves
        const isPossible = possibleMoves.some(m => m.row === row && m.col === col);
        if (isPossible) {
          const newBoard = [...board];
          newBoard[selectedStone.row][selectedStone.col] = null;
          newBoard[row][col] = currentPlayer;
          setBoard(newBoard);
          setSelectedStone({ row, col }); // Keep the stone selected for wall placement
          setTurnAction('wall');
        }
      }
    }
  }, [
    gameOver,
    gameMode,
    currentPlayer,
    playerColor,
    turnAction,
    board,
    selectedStone,
    possibleMoves,
    stonesPlaced,
    totalStonesPlaced,
  ]);

  // AI move effect
  const makeAIMove = useCallback(() => {
    const aiMove = aiType === 'random' ? makeRandomMove : makeNormalMove;

    aiMove({
      turnAction,
      board,
      currentPlayer,
      walls,
      separatedCellsGroups,
      selectedStone,
      handleCellClick,
      handleWallClick
    });
  }, [turnAction, board, currentPlayer, walls, separatedCellsGroups, selectedStone, handleCellClick, handleWallClick, aiType]);

  useEffect(() => {
    if (gameMode === 'ai' && currentPlayer !== playerColor && !gameOver) {
      const timer = setTimeout(() => makeAIMove(), 300 + Math.floor(Math.random() * 700)); // Random delay between 300ms and 1000ms
      return () => clearTimeout(timer);
    }
  }, [gameMode, currentPlayer, playerColor, gameOver, turnAction, makeAIMove]);

  // Update possibleMoves when selectedStone, board, walls, or turnAction changes
  useEffect(() => {
    if (selectedStone && turnAction === 'move') {
      setPossibleMoves(getPossibleMoves(board, walls, selectedStone.row, selectedStone.col));
    } else {
      setPossibleMoves([]);
    }
  }, [selectedStone, board, walls, turnAction]);

  // Update possibleWalls when selectedStone, walls, or turnAction changes
  useEffect(() => {
    if (selectedStone && turnAction === 'wall') {
      setPossibleWalls(getPossibleWalls(walls, selectedStone));
    } else {
      setPossibleWalls([]);
    }
  }, [selectedStone, walls, turnAction]);

  // Update separated cells groups when board or walls change
  useEffect(() => {
    // Evaluation is only relevant on move phase.
    if (turnAction !== 'move') return;

    const newSeparatedCellsGroups = findSeparatedCells(board, walls)

    setSeparatedCellsGroups(newSeparatedCellsGroups)
  }, [board, walls, turnAction]);

  // Check if game over
  useEffect(() => {
    if (!separatedCellsGroups) return;

    const calculateScores = () => {
      if (!separatedCellsGroups.every((group) => group.settled)) return null;

      const newScores = { black: 0, white: 0 }

      for (const group of separatedCellsGroups) {
        if (group.settled && group.player) {
          newScores[group.player] += group.points
        }
      }

      return newScores
    }

    const newScores = calculateScores(separatedCellsGroups)

    if (newScores) {
      const isDraw = newScores.black === newScores.white;

      setScores(newScores)
      setWinner(isDraw ? 'draw' : newScores.black > newScores.white ? 'black' : 'white');
      setGameOver(true);
    }
  }, [separatedCellsGroups]);

  const handleRestart = useCallback(() => {
    setBoard(Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null)));
    setWalls({
      horizontal: Array(BOARD_SIZE + 1).fill(null).map(() => Array(BOARD_SIZE).fill(null)),
      vertical: Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE + 1).fill(null))
    });
    setCurrentPlayer('black');
    setTurnAction('place');
    setSelectedStone(null);
    setStonesPlaced({ black: 0, white: 0 });
    setTotalStonesPlaced(0);
    setTimeLeft(INITIAL_TIME);
    setGameOver(false);
    setWinner(null);
    setScores(null);
    setPossibleMoves([]);
    setSeparatedCellsGroups(null);
    setPossibleWalls([]);
  }, []);

  return (
    <div className="flex text-white h-full">
      <div className="flex flex-col items-center justify-center flex-1 min-h-0 p-4">
        <Players
          gameMode={gameMode}
          currentPlayer={currentPlayer}
          playerColor={playerColor}
          stonesPlaced={stonesPlaced}
          timeLeft={timeLeft}
          gameOver={gameOver}
          winner={winner}
          scores={scores}
          aiType={aiType}
          currentStyle={currentStyle}
          className="hidden sm:flex"
        />
        <div
          className="flex-1 w-full h-full flex items-center justify-center flex-col sm:flex-row"
          style={{
            minWidth: 0,
            minHeight: 0
          }}
        >
          {/* Responsive wrapper for Board */}
          {useMediaQuery('(max-width: 639px)') ? (
            <div className="w-full h-full flex flex-col items-center justify-center">

              <Players
                gameMode={gameMode}
                currentPlayer={currentPlayer}
                playerColor={playerColor}
                stonesPlaced={stonesPlaced}
                timeLeft={timeLeft}
                gameOver={gameOver}
                winner={winner}
                scores={scores}
                aiType={aiType}
                currentStyle={currentStyle}
              />
              <div className="aspect-square w-[88%] sm:w-full sm:h-full">
                <Board
                  board={board}
                  walls={walls}
                  onCellClick={handleCellClick}
                  onWallClick={handleWallClick}
                  selectedStone={selectedStone}
                  turnAction={turnAction}
                  currentPlayer={currentPlayer}
                  timeLeft={timeLeft}
                  possibleMoves={possibleMoves}
                  possibleWalls={possibleWalls}
                  separatedCellsGroups={separatedCellsGroups}
                  gameOver={gameOver}
                  currentStyle={currentStyle}
                />

              </div>

              <GameSidebar onRestart={handleRestart} onStyleChange={setCurrentStyle} currentStyle={currentStyle} />
            </div>
          ) : <>
            <Board
              board={board}
              walls={walls}
              onCellClick={handleCellClick}
              onWallClick={handleWallClick}
              selectedStone={selectedStone}
              turnAction={turnAction}
              currentPlayer={currentPlayer}
              timeLeft={timeLeft}
              possibleMoves={possibleMoves}
              possibleWalls={possibleWalls}
              separatedCellsGroups={separatedCellsGroups}
              gameOver={gameOver}
              currentStyle={currentStyle}
            />

            <GameSidebar onRestart={handleRestart} onStyleChange={setCurrentStyle} currentStyle={currentStyle} />
          </>
          }
        </div>
      </div >
    </div >
  );
};

export default Game; 
